import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import ComplianceStatus from "../modals/ComplianceStatus";
import UpdateConfirmationDialog from "../modals/UpdateConfirmationDialog";
import RangeDeSelectCalendar from "../RangeDeSelectCalendar";
import { LogsTable } from "./LogsTable";
import "../../assets/css/Global.css";
import "../../assets/css/UserTable.css"
import { useNavigate } from "react-router-dom";
import Spinner from "../Spinner";
import { FaDownload } from "react-icons/fa6";
import { addAuditLogs, fetchActivities, updateActivities, fetchActivitySummaries, addAnalysisAuditLog } from "../../services/apiServices";
import { jwtDecode } from "jwt-decode";
import { processActivities } from "../../utils/activityUtils";
import { IoStatsChart } from "react-icons/io5";
import { toast } from "react-toastify";
import { UserAttributeDropdown } from "../UserAttributeDropdown";
import { getUserAndGeneratePDF } from "../../report-logic/pdf-report";
import { generateAverageSummaryPDF } from "../../report-logic/average-summary-pdf";
import { TbLogs, TbChartBar } from "react-icons/tb";
import { fetchAdminLogs } from "../../services/apiServices";
import { LuSearchX, LuSearch } from "react-icons/lu";
import {
  RUN_RESULT_OPTIONS,
  STATUS_OPTIONS,
  getOptionLabel,
  getStatusStyle,
  getRunResultStyle,
} from "../../utils/activityUtils";
import { MdClearAll } from "react-icons/md";
import { SearchFilter } from "../SearchFilter";
import Pagination from "../Pagination";
import { isAdmin } from "../../services/config";
const showToast = (message, type = "error", options = {}) => {
  const toastOptions = {
    autoClose: type === "error" ? 3000 : 2000,
    toastId: `${type}-${message}`,
    ...options,
  };
  if (type === "success") {
    toast.success(message, toastOptions);
  } else if (type === "info") {
    toast.info(message, toastOptions);
  } else {
    toast.error(message, toastOptions);
  }
};

export default function UserTable({
  title = "",
  rows = [],
  emptyMessage = "No rows to display",
  onExportFileName = "subject id data.csv",
  isLoading = false,
  onUserUpdate,
  selectedSitename
}) {
  const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [activitiesData, setActivitiesData] = useState({});
  const [loadingActivities, setLoadingActivities] = useState({});
  const [displayRows, setDisplayRows] = useState(rows);
  const [statusHighlights, setStatusHighlights] = useState({});
  const [runResultHighlights, setRunResultHighlights] = useState({});
  const highlightTimersRef = useRef([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [includeTest, setIncludeTest] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
  const [expandedLogs, setExpandedLogs] = useState({});
  const [filter, setFilter] = useState(false);
  const [editingClinicId, setEditingClinicId] = useState(null);
  const [openCalendarId, setOpenCalendarId] = useState(null);
  const calendarRef = useRef(null);


  const [endDate, setEndDate] = useState(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const y = yesterday.getFullYear();
    const m = String(yesterday.getMonth() + 1).padStart(2, '0');
    const d = String(yesterday.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7); // 6 days before yesterday = 7 days ago
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  });

  const [userCustomDates, setUserCustomDates] = useState({});

  const [globalDeselected, setGlobalDeselected] = useState(new Set());

  const [globalAdded, setGlobalAdded] = useState(new Set());
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [reportType, setReportType] = useState("average");
  const [pdfFileName, setPdfFileName] = useState("");

  const toLocalStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  /** Format a YYYY-MM-DD string for display safely */
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr + "T00:00:00").toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatLastEntry = (raw) => {
    if (!raw || raw === 'No Activity' || raw === 'Error') return raw;

    const parts = raw.split(',');
    if (parts.length >= 2) {
      let datePart = parts[0].trim();
      let timePart = parts.slice(1).join(',').trim();

      const dateMatch = datePart.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (dateMatch) {
        const day = dateMatch[1];
        const month = dateMatch[2];
        const year = dateMatch[3];
        // Try parsing assuming DD/MM/YYYY
        const d = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T12:00:00`);
        if (!isNaN(d.getTime())) {
          datePart = d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
        } else {
          // Fallback if it was MM/DD/YYYY
          const d2 = new Date(`${year}-${day.padStart(2, '0')}-${month.padStart(2, '0')}T12:00:00`);
          if (!isNaN(d2.getTime())) {
            datePart = d2.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
          }
        }
      }
      return { date: datePart, time: timePart.toUpperCase() };
    }
    const spaceParts = raw.split(' ');
    if (spaceParts.length >= 2) {
      return { date: spaceParts[0], time: spaceParts.slice(1).join(' ').toUpperCase() };
    }

    return raw;
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setOpenCalendarId(null);
      }
    }
    if (openCalendarId) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openCalendarId]);



  const maxEndObj = new Date(startDate + "T00:00:00");
  maxEndObj.setDate(maxEndObj.getDate() + 7);
  const maxEndDate = maxEndObj.toISOString().split('T')[0];

  const getDisplayDates = () => {
    let minD = startDate;
    let maxD = endDate;
    if (globalAdded && globalAdded.size > 0) {
      globalAdded.forEach(d => {
        if (d < minD) minD = d;
        if (d > maxD) maxD = d;
      });
    }
    return { start: minD, end: maxD };
  };

  const getGlobalDayCount = () => {
    const start = new Date(startDate + "T00:00:00");
    const end = new Date(endDate + "T00:00:00");
    const dateStrings = [];
    let current = new Date(start);
    while (current <= end) {
      dateStrings.push(toLocalStr(current));
      current.setDate(current.getDate() + 1);
    }
    globalAdded.forEach(d => {
      if (!dateStrings.includes(d)) dateStrings.push(d);
    });
    return dateStrings.filter(d => !globalDeselected.has(d)).length;
  };

  const computeAverages = (userId) => {
    const data = activitiesData[userId];
    if (!data || !data.rawSummaries) return null;

    const custom = userCustomDates[userId];
    const rangeStart = custom?.start || startDate;
    const rangeEnd = custom?.end || endDate;
    // Strictly use global deselected dates for all calculations
    const deselectedSet = globalDeselected;
    const addedSet = globalAdded;

    const end = new Date(rangeEnd + "T00:00:00");
    const start = new Date(rangeStart + "T00:00:00");

    const dateStrings = [];
    let current = new Date(start);
    while (current <= end) {
      const yyyy = current.getFullYear();
      const mm = String(current.getMonth() + 1).padStart(2, '0');
      const dd = String(current.getDate()).padStart(2, '0');
      dateStrings.push(`${yyyy}-${mm}-${dd}`);
      current.setDate(current.getDate() + 1);
    }
    // Add extra days from globalAdded that are outside the range
    addedSet.forEach(dateStr => {
      if (!dateStrings.includes(dateStr)) dateStrings.push(dateStr);
    });

    let voidsSum = 0, leaksSum = 0, intakeSum = 0, nightSum = 0, count = 0;
    dateStrings.forEach(dateStr => {
      if (deselectedSet.has(dateStr)) return;
      const rec = data.rawSummaries.find(s => s.date === dateStr);
      const isIncluded = (!rec || rec.dayInclude !== "no");
      if (isIncluded) {
        voidsSum += rec?.voidActivityCount || 0;
        leaksSum += rec?.totalLeaks || 0;
        intakeSum += rec?.intakeMl || 0;
        nightSum += rec?.nightVoids || 0;
        count++;
      }
    });

    return count === 0 ? null : {
      voids: (voidsSum / count).toFixed(1),
      leaks: (leaksSum / count).toFixed(1),
      fluid: (intakeSum / count).toFixed(0),
      night: (nightSum / count).toFixed(1),

    };
  };

  const toggleLogs = (userId) => {
    setExpandedLogs((prev) => {
      const isExpanded = prev[userId]?.expanded || false;
      if (isExpanded) {
        return { ...prev, [userId]: { ...prev[userId], expanded: false } };
      }
      if (!prev[userId]?.logs) {
        fetchAdminLogs(1, 50, userId).then((logs) => {
          setExpandedLogs((current) => ({
            ...current,
            [userId]: { logs: logs || [], loading: false, expanded: true },
          }));
        });
        return {
          ...prev,
          [userId]: { logs: [], loading: true, expanded: true },
        };
      }
      return { ...prev, [userId]: { ...prev[userId], expanded: true } };
    });
  };
  const handleUpdateWithConfirmation = (
    userId,
    attribute,
    newValue,
    successMessage
  ) => {
    setPendingUpdate({ userId, attribute, newValue, successMessage });
    setShowConfirmation(true);
  };

  const handleConfirmUpdate = async ({ includeInactive, notes }) => {
    if (!pendingUpdate) return;
    const { userId, attribute, newValue, successMessage } = pendingUpdate;
    const updates = { [attribute]: newValue };
    if (includeInactive) {
      updates.status = "inactive";
    }
    if (notes) {
      updates.admin_notes = notes;
    }
    try {
      setLoadingActivities((prev) => ({ ...prev, [userId]: true }));
      const response = await updateActivities(userId, updates);
      if (response && (response.success || response.data?.success)) {
        setDisplayRows((prev) =>
          prev.map((row) =>
            row.user?.id === userId
              ? { ...row, user: { ...row.user, ...updates } }
              : row
          )
        );
        if (attribute === "status" || includeInactive) {
          addHighlight(setStatusHighlights, userId);
        }
        if (attribute === "run_result") {
          addHighlight(setRunResultHighlights, userId);
        }
        showToast(successMessage, "success");
      }
    } catch (error) {
      showToast(
        `Error updating: ${error?.message || "Unknown error"}`,
        "error"
      );
    } finally {
      setLoadingActivities((prev) => ({ ...prev, [userId]: false }));
      setShowConfirmation(false);
      setPendingUpdate(null);
    }
  };
  const navigate = useNavigate();
  useEffect(() => setDisplayRows(rows), [rows]);
  useEffect(() => () => highlightTimersRef.current.forEach(clearTimeout), []);
  const addHighlight = (setter, userId) => {
    setter((prev) => ({ ...prev, [userId]: true }));
    const timerId = setTimeout(() => {
      setter((prev) => {
        if (!prev[userId]) return prev;
        const next = { ...prev };
        delete next[userId];
        return next;
      });
    }, 1600);
    highlightTimersRef.current.push(timerId);
  };
  const handleUpdateAttribute = async (
    userId,
    attribute,
    newValue,
    successMessage
  ) => {
    try {
      setLoadingActivities((prev) => ({ ...prev, [userId]: true }));
      const response = await updateActivities(userId, {
        [attribute]: newValue,
      });
      if (response && (response.success || response.data?.success)) {
        setDisplayRows((prev) =>
          prev.map((row) =>
            row.user?.id === userId
              ? { ...row, user: { ...row.user, [attribute]: newValue } }
              : row
          )
        );
        if (attribute === "status") addHighlight(setStatusHighlights, userId);
        if (attribute === "run_result")
          addHighlight(setRunResultHighlights, userId);
        showToast(successMessage || "Updated successfully!", "success");
        return;
      }
    } catch (error) {
      showToast(
        `Error updating ${attribute}: ${error?.message || "Unknown"}`,
        "error"
      );
    } finally {
      setLoadingActivities((prev) => ({ ...prev, [userId]: false }));
    }
  };
  const updateAttributeDebounced = useRef({});
  const handleCheckboxChange = (
    userId,
    attribute,
    newValue,
    successMessage
  ) => {
    setDisplayRows((prev) =>
      prev.map((row) =>
        row.user?.id === userId
          ? { ...row, user: { ...row.user, [attribute]: newValue } }
          : row
      )
    );
    onUserUpdate({
      ...rows.find((row) => row.user.id === userId).user,
      [attribute]: newValue,
    });
    if (updateAttributeDebounced.current[userId]) {
      clearTimeout(updateAttributeDebounced.current[userId]);
    }
    updateAttributeDebounced.current[userId] = setTimeout(() => {
      handleUpdateAttribute(userId, attribute, newValue, successMessage);
    }, 300);
  };
  // Track the last fetched timestamp for each user
  const lastFetchedRef = useRef({});
  useEffect(() => {
    const userIds = rows
      .map((row) => row.user?.id)
      .filter((id) => typeof id !== "undefined" && id !== null);

    if (!userIds.length) {
      setActivitiesData({});
      setLoadingActivities({});
      return;
    }
    const usersToFetch = userIds.filter((userId) => {
      const row = rows.find((r) => r.user?.id === userId);
      if (!row || Number(row.user?.all_time_activity_count) === 0) return false;
      const lastUpdated = row.user?.updatedAt || 0;
      const lastFetched = lastFetchedRef.current[userId] || 0;
      return !lastFetched || lastUpdated > lastFetched;
    });
    if (usersToFetch.length === 0) {
      return;
    }
    const newLoadingState = { ...loadingActivities };
    usersToFetch.forEach((userId) => {
      newLoadingState[userId] = true;
    });
    setLoadingActivities(newLoadingState);
    let isActive = true;
    const fetchAllActivities = async () => {
      const fetchPromises = usersToFetch.map(async (userId) => {
        if (!userId) return null;
        try {
          const userObj = rows.find(r => r.user?.id === userId)?.user || {};

          let safeSummaries = [];
          if (Number(userObj.all_time_activity_count) > 0) {
            const summaries = await fetchActivitySummaries(userId);
            safeSummaries = Array.isArray(summaries) ? summaries : [];
          }

          let parsedIssues = [];
          try {
            parsedIssues = JSON.parse(userObj.compliance_issues || "[]");
          } catch (e) { }

          const processed = {
            totalActivities: `${userObj.today_activity_count || 0}/${userObj.all_time_activity_count || 0}`,
            latestActivity: userObj.latestActivity || "No Activity",
            drinkCount: userObj.today_drink_count || 0,
            peeCount: userObj.today_pee_count || 0,
            before10AM: userObj.today_before_10am || "No",
            beforeNoon: userObj.today_before_noon || "No",
            morningUrination: userObj.today_morning_urination || "No",
            hasDuplicates: userObj.today_duplicates || 0,
            complianceStatus: userObj.compliance_status || "no-data",
            complianceIssues: parsedIssues,
            diaryStatusLabel: userObj.diary_status_label || "Not Started"
          };

          processed.rawSummaries = safeSummaries;

          return {
            userId,
            data: processed,
          };
        } catch (error) {
          showToast(
            `Error fetching activities for user ${userId}: ${error?.message || "Unknown"
            }`
          );
          return {
            userId,
            data: {
              totalActivities: "0/0",
              latestActivity: "Error",
              drinkCount: 0,
              peeCount: 0,
              before10AM: "Error",
              beforeNoon: "Error",
              morningUrination: "Error",
              hasDuplicates: 0,
              complianceStatus: "no-data",
              complianceIssues: ["Error fetching data"],
            },
          };
        }
      });
      const results = await Promise.all(fetchPromises);
      if (!isActive) return;
      // Update the last fetched timestamp for each user
      const now = Date.now();
      results.forEach((result) => {
        if (result) {
          lastFetchedRef.current[result.userId] = now;
        }
      });
      setActivitiesData((prev) => ({
        ...prev,
        ...results.reduce((acc, result) => {
          if (result) {
            acc[result.userId] = result.data;
          }
          return acc;
        }, {}),
      }));
      setLoadingActivities((prev) => ({
        ...prev,
        ...usersToFetch.reduce(
          (acc, userId) => ({
            ...acc,
            [userId]: false,
          }),
          {}
        ),
      }));
    };
    fetchAllActivities();
    return () => {
      isActive = false;
    };
  }, [rows]);
  useEffect(() => setPage(1), [search]);
  const filteredRows = useMemo(() => {
    const q = search.toLowerCase();
    return displayRows.filter((r) => {
      const isArchived = Number(r.user?.is_archived) === 1;
      const isTest = Number(r.user?.is_test) === 1;
      if (!includeArchived && isArchived) return false;
      if (!includeTest && isTest) return false;
      const u = r.user || {};
      const text = `${u.id ?? ""} ${u.name ?? ""} ${u.email ?? ""} ${r.site ?? ""
        }`.toLowerCase();
      return !q || text.includes(q);
    });
  }, [displayRows, search, includeArchived, includeTest]);
  const totalResults = filteredRows.length;
  const pageCount = Math.max(1, Math.ceil(totalResults / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  const renderActivitySummary = (userId) => {
    const a = activitiesData[userId];
    if (!a) return "—";

    const yesNo = (val) => (val === "Yes" ? "Yes" : "No");

    return (
      <div className="activity-summary">
        <div className="activity-grid">
          <div className="metric">
            <div className="metric-value">{a.totalActivities}</div>
            <div className="metric-label">Total</div>
          </div>
          <div className="metric">
            <div className="metric-value">{a.drinkCount}</div>
            <div className="metric-label">Drink</div>
          </div>
          <div className="metric">
            <div className="metric-value">{a.peeCount}</div>
            <div className="metric-label">Void</div>
          </div>
          <div className="metric">
            <div
              className={`metric-flag ${a.morningUrination === "Yes" ? "yes" : "no"
                }`}
            >
              {yesNo(a.morningUrination)}
            </div>
            <div className="metric-label">Morning</div>
          </div>
          <div className="metric">
            <div
              className={`metric-flag ${a.hasDuplicates > 0 ? "warn" : "ok"}`}
            >
              {a.hasDuplicates > 0 ? a.hasDuplicates : "0"}
            </div>
            <div className="metric-label">Dupes</div>
          </div>
          <div className="metric">
            <div
              className={`metric-flag ${a.before10AM === "Yes" ? "yes" : "no"}`}
            >
              {yesNo(a.before10AM)}
            </div>
            <div className="metric-label">Before 10</div>
          </div>
          <div className="metric">
            <div
              className={`metric-flag ${a.beforeNoon === "Yes" ? "yes" : "no"}`}
            >
              {yesNo(a.beforeNoon)}
            </div>
            <div className="metric-label">Before 12</div>
          </div>
        </div>
      </div>
    );
  };

  const exportCsv = async () => {
    const showRunIn = loggedInUser?.source !== "user";
    const headers = [
      "Subject ID",
      "Clinic",
      "Status",
    ];
    if (showRunIn) {
      headers.push("Run In Result");
    }
    headers.push(
      "Archive",
      "Test",
      "Day/All Count",
      "Latest Activity Time",
      "Drink Count",
      "Pee Count",
      "Before 10 AM",
      "Before 12 Noon",
      "Morning Urination",
      "Duplicate",
      "Compliance Status",
      "Compliance Issues"
    );

    const csvRows = [headers];

    for (const r of filteredRows) {
      const userId = r.user?.id;
      const activityData = activitiesData[userId] || {};
      const row = [
        r.user?.email || r.user?.id || "—",
        r.user?.clinic_name || "—",
        getOptionLabel(STATUS_OPTIONS, r.user?.status),
      ];
      if (showRunIn) {
        row.push(getOptionLabel(RUN_RESULT_OPTIONS, r.user?.run_result));
      }
      row.push(
        r.user.is_archived,
        r.user.is_test,
        activityData.totalActivities || "0/0",
        activityData.latestActivity || "No Activity",
        activityData.drinkCount ?? 0,
        activityData.peeCount ?? 0,
        activityData.before10AM || "No",
        activityData.beforeNoon || "No",
        activityData.morningUrination || "No",
        activityData.hasDuplicates ?? 0,
        activityData.complianceStatus || "no-data",
        Array.isArray(activityData.complianceIssues)
          ? activityData.complianceIssues
            .map((issue) => {
              // Map verbose issue descriptions to concise CSV-friendly versions
              const csvIssueMap = {
                "Missing diary entries (4+ hour gap)":
                  "Missing diary (4h+ gap)",
                "Missing void entries (6+ hour gap)":
                  "Missing void (6h+ gap)",
                "No urine output within 2 hours of waking":
                  "No morning void (2h)",
                "No activity data": "No data",
                "No compliance issues": "Compliant",
              };
              return csvIssueMap[issue] || issue;
            })
            .join("; ")
          : "—"
      );
      csvRows.push(row);
    }

    const csv = csvRows
      .map((row) =>
        row
          .map((c) =>
            String(c ?? "").includes(",")
              ? `"${String(c ?? "").replace(/"/g, '""')}"`
              : c
          )
          .join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = onExportFileName;
    a.click();
    URL.revokeObjectURL(url);
    const res = await addAuditLogs({ new_data: { fileName: onExportFileName, selectedSitename: selectedSitename }, action_type: "csv download" });

  };

  const clearAll = () => {
    setSearch("");
    setIncludeArchived(false);
    setIncludeTest(false);
  };
  const DashboardActivitiesLoading =
    Object.values(loadingActivities).some(Boolean);

  const handleDownloadReport = (userId) => {
    const activity = activitiesData[userId];
    if (!activity || activity.totalActivities === "0/0") {
      alert("No activities found to open report or download.");
      return;
    }
    getUserAndGeneratePDF(userId, activitiesData[userId]);
  };
  const handleDownloadCsv = () => {
    if (totalResults === 0) {
      alert("No activities found to download CSV.");
      return;
    }
    exportCsv();
  };

  const handleDownloadAvgPDF = async () => {
    if (filteredRows.length === 0) {
      alert("No patient rows to include in the report.");
      return;
    }
    const formatPDFDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr + "T00:00:00");
      return `${d.toLocaleDateString('en-US', { month: 'short' })}-${d.getDate()}-${d.getFullYear()}`;
    };
    const safeSite = (selectedSitename || "AllSites").replace(/[\s_]+/g, "-").replace(/[^a-zA-Z0-9-]/g, "-");
    const displayDates = getDisplayDates();
    const dateRangeStr = `${formatPDFDate(displayDates.start)}-to-${formatPDFDate(displayDates.end)}`;
    setPdfFileName(`${safeSite}-summary-${dateRangeStr}`);
    setShowDownloadModal(true);
  };

  const executeDownloadAvgPDF = async () => {
    const onlyAverages = reportType === "average";
    setShowDownloadModal(false);
    generateAverageSummaryPDF({
      rows: filteredRows,
      activitiesData,
      startDate,
      endDate,
      globalDeselected,
      globalAdded,
      siteName: selectedSitename || "",
      onlyAverages,
      fileName: pdfFileName,
    });

    try {
      const token = localStorage.getItem("token");
      let adminName = "Admin";
      let adminEmail = "";
      if (token) {
        const decoded = jwtDecode(token);
        adminName = decoded?.name || decoded?.sub || "Admin";
        adminEmail = decoded?.email || "";
      }
      await addAnalysisAuditLog({
        userId: "Global",
        adminEmail,
        adminName,
        action: `Avg Summary PDF Download (${onlyAverages ? 'Averages Only' : 'All Details'})`,
        source: "Avg Summary PDF",
      });
    } catch (e) {
      console.error("Failed to log Avg Summary PDF download", e);
    }
  };
  const handleUpdateClinic = (userId, newClinicName) => {
    handleUpdateAttribute(
      userId,
      "clinic_name",
      newClinicName,
      "Clinic Name updated successfully!"
    );
  };
  const getSystemDateFormat = () => {
    const test = new Date(2024, 11, 31);
    const formatted = test.toLocaleDateString();

    const parts = formatted.split(/[\/\-\.]/).map(n => parseInt(n, 10));

    if (parts[0] === 31) return "DD/MM";
    return "MM/DD";
  };
  const parseBasedOnSystem = (raw) => {
    const match = raw.match(
      /(\d{2})\/(\d{2})\/(\d{4}),\s*(\d{1,2}):(\d{2})\s*(am|pm)/i
    );
    if (!match) return null;

    let [, p1, p2, year, hours, minutes, meridiem] = match;

    const fmt = getSystemDateFormat();

    let day, month;
    if (fmt === "DD/MM") {
      day = p1;
      month = p2;
    } else {
      month = p1;
      day = p2;
    }

    hours = parseInt(hours, 10);
    if (meridiem.toLowerCase() === "pm" && hours !== 12) hours += 12;
    if (meridiem.toLowerCase() === "am" && hours === 12) hours = 0;

    const d = new Date(
      `${year}-${month}-${day}T${String(hours).padStart(2, "0")}:${minutes}:00`
    );

    return isNaN(d.getTime()) ? null : d;
  };
  
  const getIsActiveForAnalysis = (userId) => {
    const a = activitiesData[userId];
    const raw = a?.latestActivity;
    if (!raw || raw === "No Activity" || raw === "Error") {
      return false;
    }
    const last = parseBasedOnSystem(raw);
    if (!last) {
      return false;
    }
    const diffSeconds = (Date.now() - last.getTime()) / 1000;
    if (diffSeconds > 3 * 24 * 60 * 60) {
      return false;
    }
    return true;
  };

  const getDiaryStatus = (userId) => {
    const a = activitiesData[userId];
    const label = a?.diaryStatusLabel || "Not Started";

    if (label === "Active") {
      return { label: "Active", color: "#166534", bg: "#f0fdf4" };
    }

    return { label: "Not Started", color: "#e53e3e", bg: "#fff5f5" };
  };

  return (
    <div className="table-section">
      <div className="table-header">
        <div className="header-left">
          {title && <strong>{title}</strong>}


          <div className="exclude-controls">
            <button
              className={`pill-button ${!includeArchived && !includeTest ? "active" : ""}`}
              type="button"
              aria-pressed={!includeArchived && !includeTest}
              title="Show only excluded users"
              onClick={() => {
                setIncludeArchived(false);
                setIncludeTest(false);
              }}
            >
              Excluded: {
                rows.filter(
                  (r) =>
                    Number(r.user?.is_test) === 1 ||
                    Number(r.user?.is_archived) === 1
                ).length
              }
            </button>
            <button
              aria-pressed={includeArchived}
              className={`pill-button ${includeArchived ? "active" : ""}`}
              onClick={() => setIncludeArchived((v) => !v)}
              title="Include Archived"
            >
              Include Archived
            </button>

            <button
              aria-pressed={includeTest}
              className={`pill-button ${includeTest ? "active" : ""}`}
              onClick={() => setIncludeTest((v) => !v)}
              title="Include Test"
            >
              Include Test
            </button>
          </div>

          <div className="search-container">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative' }}>
                <button
                  className="button secondary"
                  style={{
                    padding: '6px 14px',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderRadius: '20px',
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => setOpenCalendarId(openCalendarId === 'global' ? null : 'global')}
                >
                  <span style={{ fontSize: '13px', color: '#1e293b' }}>Selected Days:</span>
                  <span style={{ color: '#0a52edff' }}>
                    <span style={{ fontSize: '13px' }} >
                      {formatDate(getDisplayDates().start)} - {formatDate(getDisplayDates().end)}
                    </span>
                    <span style={{ marginLeft: '8px', color: '#2563eb', fontSize: '13px' }}>
                      ({getGlobalDayCount()} Days)
                    </span>
                  </span>
                </button>

                {openCalendarId === 'global' && (
                  <div
                    ref={calendarRef}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      zIndex: 1000,
                      marginTop: '8px',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    <RangeDeSelectCalendar
                      initialRange={{
                        from: startDate,
                        to: endDate
                      }}
                      initialDeselected={globalDeselected}
                      initialAdded={globalAdded}
                      onConfirm={({ from, to, deselected, added }) => {
                        setStartDate(toLocalStr(from));
                        setEndDate(toLocalStr(to));
                        setGlobalDeselected(new Set(deselected.map(d => toLocalStr(d))));
                        setGlobalAdded(new Set((added || []).map(d => toLocalStr(d))));
                        setOpenCalendarId(null);
                      }}
                      onClose={() => setOpenCalendarId(null)}
                    />
                  </div>
                )}
              </div>
            </div>

            <button
              className="filter-icon"
              onClick={() => setFilter((v) => !v)}
            >
              {filter ? <LuSearchX /> : <LuSearch />}
            </button>

            <div className={`search-filter-inline ${filter ? "open" : ""}`}>
              <SearchFilter
                filter={filter}
                search={search}
                onSearchChange={setSearch}
                onClear={clearAll}
                page={currentPage}
                setPage={setPage}
                pageCount={pageCount}
                pageSize={pageSize}
                setPageSize={setPageSize}
                totalCount={rows.length}
              />
            </div>
          </div>
        </div>

        <div className="actions">
          <button
            className="button secondary"
            onClick={() => {
              if (rows.length > 0) {
                const studyId = encodeURIComponent(rows[0].user.studyId);
                navigate(`/study/${studyId}`);
              }
            }}
          >
            Back to Study
          </button>

          <button className="button primary" onClick={handleDownloadCsv}>
            Download CSV
          </button>

          <button
            className="button primary"
            onClick={handleDownloadAvgPDF}
            title="Download Average Summary PDF for all visible subjects"
          >
            Download Avg Summary 
          </button>
        </div>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 14px',
        margin: '0 0 10px 0',
        background: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#92400e',
        fontWeight: 500,
      }}>
        <span style={{ fontSize: '14px' }}>*</span>
        Summary data till {''}
        <strong>
          {new Date(new Date().setDate(new Date().getDate() - 1)).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </strong>
      </div>

      {isLoading || DashboardActivitiesLoading ? (
        <div className="table-wrapper">
          <Spinner label="Loading Users" />
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="empty-state">
          <h4 style={{ margin: 0 }}>{emptyMessage}</h4>
          <p style={{ fontSize: "13px", margin: 0 }}>
            Check Archive/Test Toggles
          </p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th className="col-id">Subject ID</th>
                <th>Last Entry</th>
                <th>Diary Status</th>
                <th>Report</th>
                <th>Average Summary Analysis</th>
                <th>Status</th>
                {loggedInUser?.source !== "user" && <th>Run In Result</th>}
                <th>Admin Notes</th>
                <th>Archive</th>
                <th>Test</th>
                <th>Activity Summary</th>
                <th>Compliance</th>
                <th>Audit</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((r) => {
                const userId = r.user?.id;
                const navigateToUserPage = (e) => {
                  e.stopPropagation();
                  navigate(
                    `/study/${encodeURIComponent(
                      r.user.studyId
                    )}/${encodeURIComponent(
                      r.user.siteId
                    )}/${encodeURIComponent(r.user.id)}`
                  );
                };

                const averages = computeAverages(userId);

                return (
                  <React.Fragment key={userId}>
                    <tr key={`${r.site}-${userId}`}>
                      <td data-label="Subject ID" className="cell-id">
                        <div onClick={navigateToUserPage} style={{ cursor: 'pointer' }}>
                          {r.user?.email && r.user?.studyId && r.user?.siteId ? (
                            <span className="id-link">{r.user.email}</span>
                          ) : (
                            r.user?.email || r.user?.id || "—"
                          )}
                        </div>

                      </td>

                      <td data-label="Last Entry">
                        {loadingActivities[userId] ? (
                          <span style={{ color: "#aaa", fontSize: "12px" }}>—</span>
                        ) : (() => {
                          const latest = activitiesData[userId]?.latestActivity || r.user?.latestActivity;
                          if (latest && latest !== "No Activity" && latest !== "Error") {
                            const entry = formatLastEntry(latest);
                            if (entry && typeof entry === 'object') {
                              return (
                                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                                  <span style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>{entry.date}</span>
                                  <span style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>{entry.time}</span>
                                </span>
                              );
                            }
                            return <span style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>{entry}</span>;
                          }
                          return <span style={{ fontSize: "10px", fontWeight: 300 }}>No activities</span>;
                        })()}
                      </td>

                      <td data-label="Diary Status">
                        {loadingActivities[userId] ? (
                          <span style={{ color: "#aaa", fontSize: "12px" }}>—</span>
                        ) : (
                          (() => {
                            const ds = getDiaryStatus(userId);
                            return (
                              <span style={{
                                display: "inline-block", padding: "2px 10px", borderRadius: "12px",
                                fontSize: "11px", fontWeight: 600, color: ds.color, background: ds.bg,
                                border: `1px solid ${ds.color}`, whiteSpace: "nowrap",
                              }}>
                                {ds.label}
                              </span>
                            );
                          })()
                        )}
                      </td>
                      <td data-label="Report">
                        <div className="action-buttons">
                          <span
                            className="icon-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadReport(userId);
                            }}
                            title="Download Report"
                          >
                            <FaDownload size={20} />
                          </span>
                        </div>
                      </td>
                      <td data-label="7-Day Analysis" style={{ textAlign: 'left', paddingLeft: '0' }}>
                        {r.user?.status === "active" ? (
                          averages ? (
                            getIsActiveForAnalysis(userId) ? (
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/study/${encodeURIComponent(r.user.studyId)}/${encodeURIComponent(r.user.siteId)}/analysis/${encodeURIComponent(r.user.id)}`
                                  );
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '10px',
                                  cursor: 'pointer',
                                  padding: '2px 10px',
                                  borderRadius: '8px',
                                  border: '1px solid #16a34a',
                                  backgroundColor: '#f0fdf4',
                                  color: '#16a34a',
                                  minWidth: '160px',
                                  marginLeft: '-4px'
                                }}
                                className="metrics-action-btn"
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: '12px',
                                    fontSize: '11px',
                                    whiteSpace: 'nowrap',
                                    width: '100%'
                                  }}
                                >
                                  <span>Voids: <b>{averages.voids}</b></span>
                                  <span>Leaks: <b>{averages.leaks}</b></span>
                                </div>
                              </div>
                            ) : (
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/study/${encodeURIComponent(r.user.studyId)}/${encodeURIComponent(r.user.siteId)}/analysis/${encodeURIComponent(r.user.id)}`
                                  );
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '10px',
                                  cursor: 'pointer',
                                  padding: '2px 10px',
                                  borderRadius: '8px',
                                  border: '1px solid #F2A412',//e5a83eff
                                  backgroundColor: '#fffbf4ff',
                                  color: '#F2A412',//e5a83eff
                                  minWidth: '160px',                               
                                  marginLeft: '-4px'
                                }}
                                className="metrics-action-btn"
                              >
                                <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                  <span style={{ fontSize: '11px', fontWeight: 'bold' }}>Inactive(View-Analysis)</span>
                                </div>
                              </div>
                            )
                          ) : (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '10px',
                                padding: '2px 10px',
                                borderRadius: '8px',
                                border: '1px solid #e53e3e',
                                backgroundColor: '#ffeeeeff',
                                color: '#e53e3e',
                                minWidth: '160px',                             
                                marginLeft: '-4px'
                              }}
                            >
                              <span style={{ fontSize: '11px', fontWeight: 500 }}>No Activities</span>
                            </div>
                          )
                        ) : (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '10px',
                              padding: '2px 10px',
                              borderRadius: '8px',
                              border: '1px solid #e53e3e',
                              backgroundColor: '#ffeeeeff',
                              color: '#e53e3e',
                              minWidth: '160px',                        
                              marginLeft: '-4px'
                            }}
                          >
                            <span style={{ fontSize: '11px', fontWeight: 500 }}>No Activities</span>
                          </div>
                        )}
                      </td>
                      <td data-label="Status">
                        {isAdmin() ? (
                          <UserAttributeDropdown
                            value={r.user.status}
                            options={STATUS_OPTIONS}
                            getStyle={getStatusStyle}
                            placeholder="Status"
                            disabled={loadingActivities[userId]}
                            isLoading={loadingActivities[userId]}
                            highlighted={!!statusHighlights[userId]}
                            onSelect={(nextValue) =>
                              handleUpdateAttribute(
                                userId,
                                "status",
                                nextValue,
                                "Status updated successfully!"
                              )
                            }
                          />
                        ) : (
                          <span
                            onClick={() => setEditingClinicId(userId)}
                          >
                            {r?.user?.status || "—"}
                          </span>
                        )}
                      </td>
                      {loggedInUser?.source !== "user" && (
                        <td data-label="Run In Result">
                          {isAdmin() ? (
                            <UserAttributeDropdown
                              value={r.user.run_result}
                              options={RUN_RESULT_OPTIONS}
                              getStyle={getRunResultStyle}
                              placeholder="Select"
                              disabled={loadingActivities[userId]}
                              isLoading={loadingActivities[userId]}
                              highlighted={!!runResultHighlights[userId]}
                              onSelect={(nextValue) => {
                                if (nextValue === "fail") {
                                  handleUpdateWithConfirmation(
                                    userId,
                                    "run_result",
                                    nextValue,
                                    "Run In Result Updated Successfully!"
                                  );
                                } else {
                                  handleUpdateWithConfirmation(
                                    userId,
                                    "run_result",
                                    nextValue,
                                    "Run In Result Updated Successfully!"
                                  );
                                }
                              }}
                            />) : (
                            <span
                              onClick={() => setEditingClinicId(userId)}
                            >
                              {r?.user?.run_result || "—"}
                            </span>
                          )}
                        </td>
                      )}
                      <td data-label="Admin Notes" className="cell-id">
                        {r.user?.admin_notes || "—"
                        }
                      </td>
                      <td data-label="Archive">
                        <label
                          className={`toggle-checkbox ${!isAdmin() || loadingActivities[userId] ? "disabled" : ""
                            }`}
                        >
                          <input
                            type="checkbox"
                            checked={Number(r.user.is_archived) === 1}
                            disabled={!isAdmin() || loadingActivities[userId]}
                            onChange={() => {
                              const newValue =
                                Number(r.user.is_archived) === 1 ? 0 : 1;
                              handleCheckboxChange(
                                userId,
                                "is_archived",
                                Number(r.user.is_archived) ? 0 : 1,
                                newValue ? "Archived!" : "Removed Archive!"
                              );
                              onUserUpdate({
                                ...r.user,
                                is_archived: newValue,
                              });
                            }}
                          />
                          <span className="slider"></span>
                        </label>
                      </td>

                      <td data-label="Test">
                        <label
                          className={`toggle-checkbox ${!isAdmin() || loadingActivities[userId] ? "disabled" : ""
                            }`}
                        >
                          <input
                            type="checkbox"
                            checked={Number(r.user.is_test) === 1}
                            disabled={!isAdmin() || loadingActivities[userId]}
                            onChange={() => {
                              const newValue =
                                Number(r.user.is_test) === 1 ? 0 : 1;
                              handleCheckboxChange(
                                userId,
                                "is_test",
                                Number(r.user.is_test) ? 0 : 1,
                                newValue
                                  ? "Marked as Test Flag"
                                  : "Unmarked Test Flag"
                              );
                              onUserUpdate({ ...r.user, is_test: newValue });
                            }}
                          />
                          <span className="slider"></span>
                        </label>
                      </td>
                      <td data-label="Activity Summary">{renderActivitySummary(userId)}</td>
                      <td data-label="Compliance">
                        <ComplianceStatus
                          status={activitiesData[userId]?.complianceStatus}
                          issues={
                            activitiesData[userId]?.complianceIssues || []
                          }
                        />
                      </td>
                      <td data-label="Audit">
                        <span
                          className="icon-button"
                          onClick={() => toggleLogs(userId)}
                        >
                          {expandedLogs[userId]?.expanded ? (
                            <MdClearAll color="#2563eb" size={23} />
                          ) : (
                            <TbLogs color="#2563eb" size={23} />
                          )}
                        </span>
                      </td>
                    </tr>

                    {expandedLogs[userId]?.expanded && (
                      <LogsTable expandedLogs={expandedLogs} userId={userId} capitalize={capitalize} />
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          <Pagination
            page={currentPage}
            pageCount={pageCount}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            totalCount={filteredRows.length}
          />

        </div>
      )}
      <UpdateConfirmationDialog
        isOpen={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
          setPendingUpdate(null);
        }}
        onConfirm={handleConfirmUpdate}
        title="Confirm Update"
        message={
          pendingUpdate?.newValue === "fail"
            ? "Are you sure you want to mark this as Failed?"
            : "Update user details?"
        }
        showInactiveCheckbox={pendingUpdate?.newValue === "fail"}
        showNotes={true}
      />
      {showDownloadModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: "350px", padding: "20px" }}>
            <h3 style={{ marginBottom: "15px", fontSize: "1rem" }}>PDF Report Options - {selectedSitename || "All Sites"}</h3>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontSize: "0.85rem", color: "#666" }}>
                File Name:
              </label>
              <input
                type="text"
                value={pdfFileName}
                onChange={(e) => setPdfFileName(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                  fontSize: "0.9rem"
                }}
              />
            </div>
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontSize: "0.85rem", color: "#666" }}>
                Select Report Type:
              </label>
              <select
                value={reportType}
                onChange={(e) => {
                  const newType = e.target.value;
                  setReportType(newType);
                  const formatPDFDate = (dateStr) => {
                    if (!dateStr) return "";
                    const d = new Date(dateStr + "T00:00:00");
                    return `${d.toLocaleDateString('en-US', { month: 'short' })}-${d.getDate()}-${d.getFullYear()}`;
                  };
                  const safeSite = (selectedSitename || "AllSites").replace(/[\s_]+/g, "-").replace(/[^a-zA-Z0-9-]/g, "-");
                  const displayDates = getDisplayDates();
                  const dateRangeStr = `${formatPDFDate(displayDates.start)}-to-${formatPDFDate(displayDates.end)}`;
                  setPdfFileName(newType === "average" ? `${safeSite}-summary-${dateRangeStr}` : `${safeSite}-summary-with-days-${dateRangeStr}`);
                }}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                  fontSize: "0.9rem"
                }}
              >
                <option value="average"> Average Summary </option>
                <option value="daily">Average Summary with Days</option>
              </select>
            </div>

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                className="button secondary"
                onClick={() => setShowDownloadModal(false)}
                style={{ fontSize: "0.85rem" }}
              >
                Cancel
              </button>
              <button
                className="button primary"
                onClick={executeDownloadAvgPDF}
                style={{ fontSize: "0.85rem" }}
              >
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
